import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';

const AskQuestionForm = ({ onSubmit, onCancel, isSubmitting }) => {
  const [question, setQuestion] = useState('');
  const [charCount, setCharCount] = useState(0);
  const maxChars = 500;
  const minChars = 10;

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxChars) {
      setQuestion(value);
      setCharCount(value.length);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (question.trim().length >= minChars && question.trim().length <= maxChars) {
      onSubmit(question.trim());
    }
  };

  const isValid = question.trim().length >= minChars && question.trim().length <= maxChars;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="border-primary-teal/20 bg-gradient-to-r from-primary-teal/5 to-transparent">
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="question" className="block text-sm font-medium text-neutral-900 mb-2">
                Ask the project owner a specific question
              </label>
              <textarea
                id="question"
                value={question}
                onChange={handleInputChange}
                placeholder="What would you like to know about this project? Be specific and constructive..."
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-teal focus:border-transparent resize-none transition-colors"
                rows={4}
                disabled={isSubmitting}
              />
              <div className="flex items-center justify-between mt-2">
                <div className="text-xs text-neutral-500">
                  {charCount < minChars && (
                    <span className="text-secondary-red">
                      Minimum {minChars} characters required
                    </span>
                  )}
                  {charCount >= minChars && charCount <= maxChars && (
                    <span className="text-primary-teal">
                      Good length for a clear question
                    </span>
                  )}
                </div>
                <div className={`text-xs ${
                  charCount > maxChars * 0.9 ? 'text-secondary-red' : 'text-neutral-500'
                }`}>
                  {charCount}/{maxChars}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs text-neutral-600 text-center sm:text-left">
                <span className="font-medium">Rate limit:</span> 5 questions per project per day
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 flex-1 sm:flex-none justify-center"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={!isValid || isSubmitting}
                  className="flex items-center gap-2 flex-1 sm:flex-none justify-center"
                >
                  <Send className="w-4 h-4" />
                  <span>{isSubmitting ? 'Submitting...' : 'Ask Question'}</span>
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AskQuestionForm;
